import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton,
  Box, Typography, Paper, Grid, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Divider
} from '@mui/material';
import { Close, TrendingUp, TrendingDown } from '@mui/icons-material';
import axios from 'axios';

const SLOTS_API = 'https://slots.moonraid.online/api/admin/slots';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SlotPlayerGambleMenu = ({ open, onClose, userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && userId) {
      loadPlayerData();
    }
    return () => {
      setData(null);
      setError(null);
    };
  }, [open, userId]);

  const loadPlayerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${SLOTS_API}/users/${userId}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (err) {
      setError('Ошибка загрузки данных игрока');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  // All-Time блок: берём именно all-time числа из бэка (не путать
  // с gameStats — те фильтруются по сегодняшнему дню МСК).
  const allTime = data?.allTimeSlots || { spins: 0, bets: 0, wins: 0, maxWin: 0, spinRtp: 0 };
  const totalSpins = allTime.spins || 0;
  const totalBets = allTime.bets || 0;
  const totalWins = allTime.wins || 0;
  const maxWin = allTime.maxWin || 0;
  const overallRtp = (allTime.spinRtp || 0).toFixed(1);

  // Сегодня (для секции «Сегодня»)
  const todaySpinsAgg = data?.gameStats?.reduce((s, g) => s + g.spins, 0) || 0;
  const todayBetsAgg = data?.gameStats?.reduce((s, g) => s + g.bets, 0) || 0;
  const todayWinsAgg = data?.gameStats?.reduce((s, g) => s + g.wins, 0) || 0;

  // 🎯 ГЛАВНОЕ число — КАССА (перспектива казино): + = МЫ в плюсе с юзера,
  // − = мы слили ему. = занёс − вывел − свободный баланс. Отвечает на вопрос
  // «мы в плюсе или он?». Та же метрика и тот же знак, что в таблице топ-10.
  const realPnLCasino = data?.user?.realPnLCasino ?? 0;
  // realPnL (СТАРОЕ, перспектива ИГРОКА): его игровой результат
  // (slot_wins − slot_bets) + gamble + лоялти. Показываем мелким как разбивку
  // «по играм», НЕ как заголовок (раньше путало: знак противоположен кассе).
  const realPnL = data?.user?.realPnL || 0;
  const realPnLBreakdown = data?.user?.realPnLBreakdown || null;

  // Сегодня: объединяем спины и gamble по играм
  const buildTodayTable = () => {
    if (!data?.todayStats) return [];
    const map = {};
    (data.todayStats.spins || []).forEach(s => {
      map[s.gameId] = {
        displayName: s.displayName || s.gameName,
        spins: s.spins, bets: s.bets, wins: s.wins,
        gambleRounds: 0, gambleNet: 0
      };
    });
    (data.todayStats.gamble || []).forEach(g => {
      if (!map[g.gameId]) {
        map[g.gameId] = {
          displayName: g.displayName || g.gameName,
          spins: 0, bets: 0, wins: 0,
          gambleRounds: 0, gambleNet: 0
        };
      }
      map[g.gameId].gambleRounds = g.rounds;
      map[g.gameId].gambleNet = g.net;
    });
    return Object.values(map);
  };

  // Объединяем all-time gameStats + gambleByGame
  const buildByGameTable = () => {
    if (!data?.gameStats) return [];
    const gambleMap = {};
    (data.gambleByGame || []).forEach(g => {
      gambleMap[g.gameId] = g;
    });
    return data.gameStats.map(gs => {
      const gb = gambleMap[gs.gameId] || { rounds: 0, net: 0 };
      const combinedBets = gs.bets;
      const combinedWins = gs.wins + gb.net;
      const combinedRtp = combinedBets > 0 ? ((combinedWins / combinedBets) * 100).toFixed(1) : '0';
      return {
        ...gs,
        gambleRounds: gb.rounds,
        gambleNet: gb.net,
        combinedRtp: parseFloat(combinedRtp)
      };
    });
  };

  const todayRows = buildTodayTable();
  const byGameRows = buildByGameTable();
  const todayTotalBets = todayRows.reduce((s, r) => s + r.bets, 0);
  const todayTotalWins = todayRows.reduce((s, r) => s + r.wins, 0);
  const todayGambleNet = todayRows.reduce((s, r) => s + r.gambleNet, 0);
  const todayPnl = todayTotalWins + todayGambleNet - todayTotalBets;
  const todayRtp = todayTotalBets > 0 ? (((todayTotalWins + todayGambleNet) / todayTotalBets) * 100).toFixed(1) : '0';

  // Combined all-time RTP (слоты + gamble)
  const gambleNet = data?.gambleStats?.netProfit || 0;
  const combinedAllTimeRtp = totalBets > 0 ? (((totalWins + gambleNet) / totalBets) * 100).toFixed(1) : '0';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { bgcolor: '#0f172a', color: '#fff' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span">
            {data?.user?.displayName || '...'}
          </Typography>
          {data?.user && (
            <>
              <Chip label={`ID: ${data.user.telegramId}`} size="small" variant="outlined" />
              <Chip label={`${data.user.balance.toFixed(2)} SPERM`} size="small" color="primary" />
              <Chip
                label={realPnLCasino >= 0 ? `КАЗИНО В ПЛЮСЕ +${realPnLCasino.toFixed(2)} SPERM` : `КАЗИНО В МИНУСЕ ${realPnLCasino.toFixed(2)} SPERM`}
                size="medium"
                sx={{
                  bgcolor: realPnLCasino >= 0 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                  color: realPnLCasino >= 0 ? '#4caf50' : '#f44336',
                  fontWeight: 'bold',
                  fontSize: '0.85rem'
                }}
              />
            </>
          )}
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {data && !loading && (
          <>
            {/* Секция 1: Финансовый обзор */}
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
              Финансовый обзор
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(99, 102, 241, 0.15)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Внесено</Typography>
                  <Typography variant="h6">{data.user.totalDeposited.toFixed(2)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(16, 185, 129, 0.15)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Баланс</Typography>
                  <Typography variant="h6">{data.user.balance.toFixed(2)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(239, 68, 68, 0.15)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Выведено</Typography>
                  <Typography variant="h6">{data.user.totalWithdrawn.toFixed(2)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Paper sx={{ p: 2, bgcolor: realPnLCasino >= 0 ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Реальный P&L (касса)</Typography>
                  <Typography variant="h6" sx={{ color: realPnLCasino >= 0 ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                    {realPnLCasino >= 0 ? '+' : ''}{realPnLCasino.toFixed(2)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: realPnLCasino >= 0 ? '#4caf50' : '#f44336', fontSize: '0.6rem' }}>
                    {realPnLCasino >= 0 ? 'мы в плюсе' : 'мы слили игроку'} · занёс − вывел − свободный
                  </Typography>
                  {realPnLBreakdown && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: '0.65rem' }}>
                      по играм (игрок): слоты {realPnLBreakdown.slotPnL >= 0 ? '+' : ''}{realPnLBreakdown.slotPnL.toFixed(0)} · gamble {realPnLBreakdown.gambleNet >= 0 ? '+' : ''}{realPnLBreakdown.gambleNet.toFixed(0)} · лоялти {(realPnLBreakdown.loyaltyChest + realPnLBreakdown.wagerUnlocked >= 0 ? '+' : '') + (realPnLBreakdown.loyaltyChest + realPnLBreakdown.wagerUnlocked).toFixed(0)}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Секция 2: Сегодня */}
            {todayRows.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                  Сегодня
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(30, 41, 59, 0.5)', mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Игра</TableCell>
                        <TableCell align="right">Спины</TableCell>
                        <TableCell align="right">Ставки</TableCell>
                        <TableCell align="right">Выигрыши</TableCell>
                        <TableCell align="right">Gamble</TableCell>
                        <TableCell align="right">Gamble Net</TableCell>
                        <TableCell align="right">P&L</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {todayRows.map((r, i) => {
                        const pnl = r.wins + r.gambleNet - r.bets;
                        return (
                          <TableRow key={i}>
                            <TableCell>{r.displayName}</TableCell>
                            <TableCell align="right">{r.spins}</TableCell>
                            <TableCell align="right">{r.bets.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ color: '#4caf50' }}>{r.wins.toFixed(2)}</TableCell>
                            <TableCell align="right">{r.gambleRounds || '—'}</TableCell>
                            <TableCell align="right" sx={{ color: r.gambleNet >= 0 ? '#4caf50' : '#f44336' }}>
                              {r.gambleRounds > 0 ? `${r.gambleNet >= 0 ? '+' : ''}${r.gambleNet.toFixed(2)}` : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: pnl >= 0 ? '#4caf50' : '#f44336', fontWeight: 'bold' }}>
                              {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Итого */}
                      <TableRow sx={{ bgcolor: 'rgba(99, 102, 241, 0.1)' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>ИТОГО</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{todayRows.reduce((s, r) => s + r.spins, 0)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{todayTotalBets.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#4caf50' }}>{todayTotalWins.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{todayRows.reduce((s, r) => s + r.gambleRounds, 0)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: todayGambleNet >= 0 ? '#4caf50' : '#f44336' }}>
                          {todayGambleNet >= 0 ? '+' : ''}{todayGambleNet.toFixed(2)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: todayPnl >= 0 ? '#4caf50' : '#f44336' }}>
                          {todayPnl >= 0 ? '+' : ''}{todayPnl.toFixed(2)}
                          <Chip label={`RTP ${todayRtp}%`} size="small" sx={{ ml: 1, fontSize: '0.65rem', height: 18 }} />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Секция 3: All-Time (2 карточки рядом) */}
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
              All-Time
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {/* Слоты */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(99, 102, 241, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#6366f1', fontWeight: 'bold' }}>
                    Слоты
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Спинов</Typography>
                      <Typography variant="body2" fontWeight="bold">{totalSpins.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Wagered</Typography>
                      <Typography variant="body2" fontWeight="bold">{totalBets.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Won</Typography>
                      <Typography variant="body2" fontWeight="bold" sx={{ color: '#4caf50' }}>{totalWins.toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Spin RTP</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        <Chip
                          label={`${overallRtp}%`}
                          size="small"
                          color={parseFloat(overallRtp) > 100 ? 'error' : parseFloat(overallRtp) > 90 ? 'success' : 'warning'}
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">Max Win</Typography>
                      <Typography variant="body2" fontWeight="bold">{maxWin.toFixed(2)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              {/* Gamble */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(233, 30, 99, 0.1)', height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#e91e63', fontWeight: 'bold' }}>
                    Gamble
                  </Typography>
                  {data.gambleStats.totalRounds > 0 ? (
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Раундов</Typography>
                        <Typography variant="body2" fontWeight="bold">{data.gambleStats.totalRounds}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Win Rate</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          <Chip label={`${data.gambleStats.winRate}%`} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Wins</Typography>
                        <Typography variant="body2" sx={{ color: '#4caf50' }}>{data.gambleStats.wins}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Losses</Typography>
                        <Typography variant="body2" sx={{ color: '#ef5350' }}>{data.gambleStats.losses}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Risked</Typography>
                        <Typography variant="body2">{data.gambleStats.totalRisked.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Won</Typography>
                        <Typography variant="body2" sx={{ color: '#4caf50' }}>+{data.gambleStats.totalWon.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Lost</Typography>
                        <Typography variant="body2" sx={{ color: '#ef5350' }}>-{data.gambleStats.totalLost.toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Net</Typography>
                        <Typography variant="body2" fontWeight="bold" sx={{ color: gambleNet >= 0 ? '#4caf50' : '#ef5350' }}>
                          {gambleNet >= 0 ? '+' : ''}{gambleNet.toFixed(2)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">Combined RTP (слоты + gamble)</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          <Chip
                            label={`${combinedAllTimeRtp}%`}
                            size="small"
                            color={parseFloat(combinedAllTimeRtp) > 100 ? 'error' : parseFloat(combinedAllTimeRtp) > 90 ? 'success' : 'warning'}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Нет данных gamble</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Секция 4: По играм (за сегодня, бэк фильтрует statsResult по МСК-дню) */}
            {byGameRows.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                  По играм (Сегодня)
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(30, 41, 59, 0.5)', mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Игра</TableCell>
                        <TableCell align="right">Спины</TableCell>
                        <TableCell align="right">Ставки</TableCell>
                        <TableCell align="right">Выигрыши</TableCell>
                        <TableCell align="right">Spin RTP</TableCell>
                        <TableCell align="right">Gamble</TableCell>
                        <TableCell align="right">Gamble Net</TableCell>
                        <TableCell align="right">Combined RTP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byGameRows.map((r) => (
                        <TableRow key={r.gameId}>
                          <TableCell>{r.displayName || r.gameName}</TableCell>
                          <TableCell align="right">{r.spins.toLocaleString()}</TableCell>
                          <TableCell align="right">{r.bets.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ color: '#4caf50' }}>{r.wins.toFixed(2)}</TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${r.spinRtp}%`}
                              size="small"
                              color={r.spinRtp > 100 ? 'error' : r.spinRtp > 90 ? 'success' : 'warning'}
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                          </TableCell>
                          <TableCell align="right">{r.gambleRounds > 0 ? r.gambleRounds : '—'}</TableCell>
                          <TableCell align="right" sx={{ color: r.gambleNet >= 0 ? '#4caf50' : '#f44336' }}>
                            {r.gambleRounds > 0 ? `${r.gambleNet >= 0 ? '+' : ''}${r.gambleNet.toFixed(2)}` : '—'}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${r.combinedRtp}%`}
                              size="small"
                              color={r.combinedRtp > 100 ? 'error' : r.combinedRtp > 90 ? 'success' : 'warning'}
                              sx={{ fontSize: '0.65rem', height: 18 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Секция 5: Последние gamble */}
            {data.recentGamble?.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'bold' }}>
                  Последние gamble ({data.recentGamble.length})
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(233, 30, 99, 0.08)', mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Время</TableCell>
                        <TableCell>Игра</TableCell>
                        <TableCell align="right">Рискнул</TableCell>
                        <TableCell align="center">Выбор</TableCell>
                        <TableCell align="center">Результат</TableCell>
                        <TableCell align="center">Win?</TableCell>
                        <TableCell align="right">Итого</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.recentGamble.map((g) => (
                        <TableRow key={g.id} sx={{
                          bgcolor: g.isWin ? 'rgba(76, 175, 80, 0.08)' : 'rgba(244, 67, 54, 0.08)'
                        }}>
                          <TableCell>{new Date(g.date).toLocaleString('ru-RU')}</TableCell>
                          <TableCell>{g.gameName}</TableCell>
                          <TableCell align="right">{g.originalWin.toFixed(2)}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={g.choice === 'omikuji' ? '🎴' : g.choice === 'red' ? '🔴' : '⚫'}
                              title={g.choice === 'omikuji' ? 'Омикудзи (Kaguya Ways): удвоить или потерять' : undefined}
                              size="small"
                              sx={{ fontSize: '0.8rem', height: 22, minWidth: 36, bgcolor: g.choice === 'red' ? 'rgba(244,67,54,0.2)' : g.choice === 'omikuji' ? 'rgba(216,27,96,0.2)' : 'rgba(50,50,50,0.3)' }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={g.choice === 'omikuji' ? (g.resultCard === 'kichi' ? '吉 удача' : '凶 неудача') : g.resultCard}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={g.isWin ? 'WIN' : 'LOSE'}
                              size="small"
                              sx={{
                                bgcolor: g.isWin ? '#4caf50' : '#f44336',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.65rem',
                                height: 20
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{
                            fontWeight: 'bold',
                            color: g.isWin ? '#4caf50' : '#f44336'
                          }}>
                            {g.isWin ? `+${g.finalAmount.toFixed(2)}` : '0.00'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SlotPlayerGambleMenu;
